import { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../api';
import { toast } from 'react-toastify';
import { deleteCookies } from '../utils/util';
import { useLocation, useNavigate } from 'react-router-dom';

const AuthContext = createContext({} as any);

const AuthProvider = ({ children }: any) => {
  const nav = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [confirmEmailOtpResp, setConfirmEmailOtpResp] = useState([]);
  const [subKpis, setSubKpis] = useState([]);
  const [userType, setUserType] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState({
    id: null,
    user: null,
    email: null,
    userType: null,
    company: null,
    dateJoined: null,
  });
  const location = useLocation();
  // ─── Dashboard preferences ─────────────────────────────────────
  const [dashboardPrefs, setDashboardPrefs] = useState<{
    id?: number;
    show_data_labels: boolean;
    chart_type: string;
  }>({
    show_data_labels: true,
    chart_type: 'line',
  });

  // load on mount
  useEffect(() => {
    if (location.pathname === '/login') return;
    setIsLoading(true);
    // console.log("AuthContext useEffect")
    checkImpersonation();
    API.getDashboardPreferences()
      .then((res: any) => {
        if (Array.isArray(res.data) && res.data.length) {
          setDashboardPrefs(res.data[0]);
        }
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  // create or update
  const saveDashboardPrefs = (updates: Partial<typeof dashboardPrefs>) => {
    setIsLoading(true);
    const fn = dashboardPrefs.id
      ? API.updateDashboardPreferences(dashboardPrefs.id!, updates)
      : API.createDashboardPreferences(updates);
    return fn
      .then((res: any) => setDashboardPrefs(res.data))
      .finally(() => setIsLoading(false));
  };

  const sendEmailOtp = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.sendEmailOtp(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const confirmEmailOtp = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.confirmEmailOtp(payload)
        .then((response) => {
          setConfirmEmailOtpResp(response.data);
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getRegions = () => {
    setIsLoading(true);
    API.getRegions()
      .then((response) => {
        setRegions(response.data);
      })
      .catch((error) => console.log(error))
      .finally(() => setIsLoading(false));
  };

  const userRegister = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.register(payload)
        .then((response) => {
          toast.success(response?.data?.message);
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const userLogin = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.login(payload)
        .then((response) => {
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          localStorage.setItem('company', JSON.stringify(response.data.company));
          localStorage.setItem('original_token', response.data.token);
          localStorage.setItem('original_user', JSON.stringify(response.data.user));
          localStorage.setItem('original_company', JSON.stringify(response.data.company));
          setUserType(response.data?.user?.user_type);
          setIsAuthenticated(true);
          resolve(response.data.user);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.non_field_errors[0]);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const userLogout = () => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.logout()
        .then((response) => {
          setIsAuthenticated(false);
          resolve(response);
        })
        .catch((error) => console.log(error))
        .finally(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('company');
          deleteCookies();
          setIsLoading(false);
          nav('/login');
        });
    });
  };

  const resetSendEmailOtp = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.resetSendEmailOtp(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const resetConfirmEmailOtp = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.resetConfirmEmailOtp(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getSubKpiList = () => {
    setIsLoading(true);
    API.getSubKpiList()
      .then((response) => {
        setSubKpis(response.data);
      })
      .catch((error) => console.log(error))
      .finally(() => setIsLoading(false));
  };

  const userResetPassword = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.resetUserPassword(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const addKpi = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.addKpi(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const editKpi = (id: any, payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.editKpi(id, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getKpiPriorities = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getKpiPriorities()
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getKpiList = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getKpiList(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getKpi = (id: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getKpi(id)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const deleteKpi = (id: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.delKpi(id)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const fetchReportData = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.fetchReportData(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const exportReportData = (payload: any, config?: any) => {
    const toastId = toast.loading('KPI Report is being exported...');
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.exportReportData(payload, config)
        .then((response) => {
          toast.update(toastId, {
            render: 'KPI Report exported successfully.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
            closeButton: true,
          });
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.update(toastId, {
            render: error?.response?.data?.message,
            type: 'error',
            isLoading: false,
            autoClose: 5000,
            closeButton: true,
          });
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const addOrEditReportData = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.addOrEditReportData(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getCompanyList = (company_ids: number[] = []) => {
    setIsLoading(true);
  
    return API.getCompanyList(company_ids)
      .then((response) => response.data)
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Failed to fetch company list");
        return []; // ✅ return empty array on error
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getScorecardData = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getScorecard(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const exportScorecardData = (payload: any, config?: any) => {
    const toastId = toast.loading('Scorecard is being exported...');
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.exportScorecard(payload, config)
        .then((response) => {
          toast.update(toastId, {
            render: 'Scorecard exported successfully.',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
            closeButton: true,
          });
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.update(toastId, {
            render: error?.response?.data?.message,
            type: 'error',
            isLoading: false,
            autoClose: 5000,
            closeButton: true,
          });
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getCustomerList = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getCustomerList(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const fetchChartData = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.fetchChartData(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const checkImpersonation = () => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.checkImpersonate()
        .then((response) => {
          resolve(response.data);
          if (response.data.is_impersonating) {
            setIsImpersonating(true);
            setImpersonatedUser({
              id: response.data.impersonated_user_id,
              user: response.data.impersonated_user,
              email: response.data.impersonated_email,
              userType: response.data.impersonated_user_type,
              company: response.data.impersonated_company,
              dateJoined: response.data.impersonated_date_joined,
            });
            // Use the impersonation token to log in as the impersonated user
            const token = response.data.impersonation_token;
            if (token) {
              localStorage.setItem('token', token);
              localStorage.setItem('user', JSON.stringify(response.data.impersonated_user));
              localStorage.setItem('company', JSON.stringify(response.data.impersonated_company));
              setIsAuthenticated(true);
            }
          } else {
            const originalToken = localStorage.getItem('original_token') || '';
            const originalUser = localStorage.getItem('original_user') || '{}';
            const originalCompany = localStorage.getItem('original_company') || '{}';
            localStorage.setItem('token', originalToken); // Set the original token
            localStorage.setItem('user', originalUser); // Set the original user data
            localStorage.setItem('company', originalCompany); // Set the original company data
            setIsImpersonating(false);
            setIsAuthenticated(true); // Set authenticated state to true
            setImpersonatedUser({ id: null, user: null, email: null, userType: null, company: null, dateJoined: null });
          }
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message || "Failed to check impersonation state.");
        })
        .finally(() => setIsLoading(false));
    });
  };
  
  const stopImpersonation = () => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.stopImpersonate()
        .then((response) => {
          resolve(response.data);
          toast.success("Impersonation stopped");
          
          const originalToken = localStorage.getItem('original_token') || '';
          const originalUser = localStorage.getItem('original_user') || '{}';
          const originalCompany = localStorage.getItem('original_company') || '{}';

          // Restore the original user's token and data
          if (originalToken) {
            localStorage.setItem('token', originalToken);
            localStorage.setItem('user', originalUser);
            localStorage.setItem('company', originalCompany);
            setIsAuthenticated(true);
          } else {
            // If no original user data is available, log out
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('company');
            setIsAuthenticated(false);
          }
          setIsImpersonating(false);
          window.location.reload(); // Reload the app to end impersonation
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message || "Failed to stop impersonation");
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getUserList = (config:any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getUsers(config)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const editUser = (id: any, payload: any) => {
    setIsLoading(true);

    return API.editUser(id, payload)
    .then((response) =>{
    toast.success("User sucessfully edited")
      
      return response.data})
    .catch((error) => {
      toast.error(error?.response?.data?.detail || "Error while editing user.");
      console.log(error);
    })
    .finally(() => {
      setIsLoading(false);
    });

  };

  const deleteUser = (id: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.delUser(id)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getFeedbackList = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getFeedbackList(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getFeedbackCount = () => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.getFeedbackCount()
        .then((response) => {
          resolve(response.data);
          setFeedbackCount(response?.data?.count);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
          setFeedbackCount(0);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const readThreads = (id: any, payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.readThreads(id, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const postFeedback = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.postFeedback(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const updateFeedback = (id: any, payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.updateFeedback(id, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const deleteFeedback = (id: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.delFeedback(id)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const replyFeedback = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.replyFeedback(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const uploadChartImage = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.uploadChartImage(payload)
        .then((response) => {
          resolve(response.data);
          toast.success('screenshot captured');
        })
        .catch((error) => {
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getContactUs = () => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getContactUs()
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };
  const postDashboard = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.postDashboard(payload)
        .then((response) => {
          resolve(response.data);
          // toast.success("Data fetched.")
        })
        .catch((error) => {
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const changePassword = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.changePassword(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const getBookmark = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve) => {
      API.getBookmark(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const addBookmark = (payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.addBookmark(payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.detail);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const updateBookmark = (id: any, payload: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.updateBookmark(id, payload)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  const deleteBookmark = (id: any) => {
    setIsLoading(true);
    return new Promise((resolve, reject) => {
      API.deleteBookmark(id)
        .then((response) => {
          resolve(response.data);
        })
        .catch((error) => {
          reject(error);
          toast.error(error?.response?.data?.message);
          console.log(error);
        })
        .finally(() => setIsLoading(false));
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isLoading,
        userLogin,
        userType,
        sendEmailOtp,
        confirmEmailOtp,
        confirmEmailOtpResp,
        getRegions,
        regions,
        userRegister,
        userLogout,
        resetSendEmailOtp,
        resetConfirmEmailOtp,
        userResetPassword,
        getSubKpiList,
        subKpis,
        addKpi,
        editKpi,
        getKpi,
        getKpiList,
        getKpiPriorities,
        getCompanyList,
        fetchReportData,
        exportReportData,
        getCustomerList,
        addOrEditReportData,
        deleteKpi,
        fetchChartData,
        getScorecardData,
        exportScorecardData,
        isImpersonating,
        impersonatedUser,
        checkImpersonation,
        stopImpersonation,
        getUserList,
        editUser,
        deleteUser,
        getFeedbackList,
        feedbackCount,
        getFeedbackCount,
        readThreads,
        postFeedback,
        updateFeedback,
        deleteFeedback,
        replyFeedback,
        uploadChartImage,
        getContactUs,
        isAuthenticated,
        postDashboard,
        changePassword,
        getBookmark,
        addBookmark,
        updateBookmark,
        deleteBookmark,
        dashboardPrefs,
        saveDashboardPrefs,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);

export default AuthProvider;
